import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Container, Input, InputWrapper, Label, Suffix } from './styles';

export interface FormProps {
  label: string;
  invalidLabel?: string;
  value: string | number | undefined;
  isRequired?: boolean;
  min?: number;
  max?: number;
  isUndefined?: boolean;
  isNull?: boolean;
  isEmpty?: boolean;
  placeholder?: string;
  onChange: (value: number | string) => void;
  setInvalid?: boolean;
  iniInicializado?: boolean;
  isDisabled?: boolean;
  autoFocus?: boolean;
  onBlur?: (event: ChangeEvent<HTMLInputElement>) => void;
  inputWidth?: string;

  /** Limita a quantidade de caracteres digitados */
  maxChars?: number;
  /** Impede zeros à esquerda (ex.: "000" → "0", "010" → "10") */
  noLeadingZeros?: boolean;
}

const InputNumberDias: React.FC<FormProps> = (props) => {
  const {
    label,
    value,
    isRequired = false,
    onChange,
    min = 0,
    max = 99999,
    isUndefined = false,
    isNull = false,
    isEmpty = false,
    placeholder = `Digite um valor para ${label}`,
    setInvalid = false,
    iniInicializado = false,
    isDisabled = false,
    autoFocus = false,
    onBlur,
    invalidLabel,
    inputWidth = '6.25rem',
    maxChars = 3,
    noLeadingZeros = true,
  } = props;

  const [inicializado, setInicializado] = useState<boolean>(false);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);

  const ids = useMemo(() => {
    const base = `input-number-dias-${label
      .replace(/\s+/g, '-')
      .toLowerCase()}`;
    return { inputId: `${base}-control`, helperId: `${base}-helper` };
  }, [label]);

  const computeInvalid = useCallback(
    (
      raw: string | number | undefined | null,
      alreadyStarted: boolean,
    ): { invalid: boolean; reason: string | null } => {
      if (isRequired && alreadyStarted) {
        if (isUndefined && raw === undefined)
          return {
            invalid: true,
            reason: invalidLabel || 'Campo obrigatório.',
          };
        if (isNull && raw === null)
          return {
            invalid: true,
            reason: invalidLabel || 'Campo obrigatório.',
          };
        if (isEmpty) {
          const str = raw == null ? '' : String(raw);
          if (str.trim() === '')
            return {
              invalid: true,
              reason: invalidLabel || 'Campo obrigatório.',
            };
        }
      }
      if (raw === '' || raw === undefined || raw === null)
        return { invalid: false, reason: null };

      const n = Number.parseFloat(String(raw));
      if (Number.isNaN(n))
        return { invalid: true, reason: 'Informe um número válido.' };
      if (alreadyStarted) {
        if (n < min) return { invalid: true, reason: `O mínimo é ${min}.` };
        if (n > max) return { invalid: true, reason: `O máximo é ${max}.` };
      }
      return { invalid: false, reason: null };
    },
    [isRequired, isUndefined, isNull, isEmpty, invalidLabel, min, max],
  );

  const validateAndNotify = useCallback(
    (nextValue: string) => {
      const { invalid } = computeInvalid(nextValue, true);
      setIsInvalid(invalid);
      setInicializado(true);
      onChange(nextValue);
    },
    [computeInvalid, onChange],
  );

  /** Remove não-dígitos, aplica maxChars e remove zeros à esquerda */
  const sanitizeNumeric = useCallback(
    (raw: string): string => {
      if (raw === '') return ''; // permite limpar o campo
      let s = raw.replace(/\D+/g, ''); // apenas dígitos

      if (typeof maxChars === 'number' && maxChars > 0) {
        s = s.slice(0, maxChars);
      }

      if (noLeadingZeros && s.length > 1) {
        // remove zeros à esquerda; se virar vazio (ex.: "000"), vira "0"
        s = s.replace(/^0+/, '') || '0';
      }

      return s;
    },
    [maxChars, noLeadingZeros],
  );

  useEffect(() => {
    const { invalid } = computeInvalid(value as any, inicializado);
    setIsInvalid(setInvalid || invalid);
  }, [value, setInvalid, invalidLabel, inicializado, computeInvalid]);

  useEffect(() => {
    setInicializado(iniInicializado);
  }, [iniInicializado]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const next = sanitizeNumeric(e.target.value ?? '');
      // Reflete no input controlado caso tenha havido saneamento
      if (next !== e.target.value) e.target.value = next;
      validateAndNotify(next);
    },
    [sanitizeNumeric, validateAndNotify],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text') ?? '';
      const next = sanitizeNumeric(pasted);
      validateAndNotify(next);
    },
    [sanitizeNumeric, validateAndNotify],
  );

  return (
    <Container data-invalid={isInvalid || undefined}>
      <Label htmlFor={ids.inputId}>
        {label}
        {isRequired ? ' *' : ''}
      </Label>

      <InputWrapper width={inputWidth}>
        <Input
          id={ids.inputId}
          type="text" // controla tamanho e máscara
          inputMode="numeric" // teclado numérico no mobile
          pattern="[0-9]*"
          autoFocus={autoFocus}
          disabled={isDisabled}
          value={value ?? ''}
          placeholder={placeholder}
          maxLength={typeof maxChars === 'number' ? maxChars : undefined}
          onChange={handleChange}
          onPaste={handlePaste}
          onBlur={onBlur}
          aria-invalid={isInvalid}
          aria-describedby={isInvalid ? ids.helperId : undefined}
        />
        <Suffix aria-hidden="true">Dias</Suffix>
      </InputWrapper>
    </Container>
  );
};

export default InputNumberDias;
